import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

import {
  asignarRol,
  editarMatriz,
  getAdminConfig,
  getAdminUsuarios,
  quitarRol,
  setNotificacionTbfin,
} from "@/lib/api";

export function AdminPage() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ["admin", "config"], queryFn: getAdminConfig });
  const usersQ = useQuery({ queryKey: ["admin", "usuarios"], queryFn: getAdminUsuarios });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Administración de perfiles</h1>
        <p className="text-sm text-slate-500 mt-1">
          Asigna un perfil a cada usuario y configura qué puede hacer cada perfil. El
          Administrador siempre tiene acceso a todo.
        </p>
      </div>

      {(cfgQ.isError || usersQ.isError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {((cfgQ.error || usersQ.error) as Error)?.message}
        </div>
      )}

      {cfgQ.data && usersQ.data && (
        <>
          <Usuarios
            roles={cfgQ.data.roles}
            usuarios={usersQ.data}
            onChanged={() => qc.invalidateQueries({ queryKey: ["admin", "usuarios"] })}
          />
          <Matriz
            roles={cfgQ.data.roles}
            permisos={cfgQ.data.permisos}
            matriz={cfgQ.data.matriz}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["admin", "config"] });
              qc.invalidateQueries({ queryKey: ["me"] });
            }}
          />
        </>
      )}
    </div>
  );
}

type Rol = { key: string; label: string; editable: boolean };

function Usuarios({
  roles,
  usuarios,
  onChanged,
}: {
  roles: Rol[];
  usuarios: { email: string; rol: string; notificar_tbfin: boolean; actualizado_en: string | null }[];
  onChanged: () => void;
}) {
  const [email, setEmail] = useState("");
  const [rol, setRol] = useState(roles[0]?.key ?? "consulta");

  const asignar = useMutation({
    mutationFn: (p: { email: string; rol: string }) => asignarRol(p.email, p.rol),
    onSuccess: () => {
      setEmail("");
      onChanged();
    },
  });
  const quitar = useMutation({
    mutationFn: (e: string) => quitarRol(e),
    onSuccess: onChanged,
  });
  const notif = useMutation({
    mutationFn: (p: { email: string; activo: boolean }) =>
      setNotificacionTbfin(p.email, p.activo),
    onSuccess: onChanged,
  });
  const labelDe = (k: string) => roles.find((r) => r.key === k)?.label ?? k;

  return (
    <section className="bg-white border rounded-lg">
      <div className="px-4 py-2 border-b text-sm font-medium text-slate-700">Usuarios y perfiles</div>
      <div className="p-4 space-y-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) asignar.mutate({ email: email.trim(), rol });
          }}
        >
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Correo</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@mppenergia.com"
              className="mt-1 border rounded px-2 py-1.5 text-sm w-72"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Perfil</span>
            <select
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              className="mt-1 border rounded px-2 py-1.5 text-sm"
            >
              {roles.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={asignar.isPending}
            className="bg-blue-600 text-white text-sm rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
          >
            Asignar
          </button>
        </form>
        {asignar.isError && (
          <div className="text-sm text-red-600">{(asignar.error as Error).message}</div>
        )}
        {quitar.isError && (
          <div className="text-sm text-red-600">{(quitar.error as Error).message}</div>
        )}
        {notif.isError && (
          <div className="text-sm text-red-600">{(notif.error as Error).message}</div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-1.5 font-medium">Correo</th>
              <th className="py-1.5 font-medium">Perfil</th>
              <th className="py-1.5 font-medium text-center">Notif. registro TBFin</th>
              <th className="py-1.5 font-medium w-10"></th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-center text-slate-400">Sin usuarios.</td></tr>
            )}
            {usuarios.map((u) => (
              <tr key={u.email} className="border-b last:border-0">
                <td className="py-1.5 text-slate-800">{u.email}</td>
                <td className="py-1.5">
                  <select
                    value={u.rol}
                    onChange={(e) => asignar.mutate({ email: u.email, rol: e.target.value })}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    {roles.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-1.5 text-center">
                  <input
                    type="checkbox"
                    checked={u.notificar_tbfin}
                    disabled={notif.isPending}
                    onChange={(e) => notif.mutate({ email: u.email, activo: e.target.checked })}
                    className="w-4 h-4 accent-blue-600"
                    title="Recibir correo cuando se registre una TBFin en CENACE"
                  />
                </td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    title={`Quitar perfil de ${u.email}`}
                    onClick={() => {
                      if (confirm(`¿Quitar el perfil de ${u.email}? Quedará sin acceso.`)) {
                        quitar.mutate(u.email);
                      }
                    }}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-slate-400">
          Perfil actual: {usuarios.map((u) => labelDe(u.rol)).filter((v, i, a) => a.indexOf(v) === i).join(", ") || "—"}.
          Quitar el perfil deja al usuario sin acceso (403) hasta reasignarlo.
        </p>
      </div>
    </section>
  );
}

function Matriz({
  roles,
  permisos,
  matriz,
  onSaved,
}: {
  roles: Rol[];
  permisos: { key: string; label: string }[];
  matriz: Record<string, Record<string, boolean>>;
  onSaved: () => void;
}) {
  const editables = roles.filter((r) => r.editable);
  const [local, setLocal] = useState<Record<string, Record<string, boolean>>>(matriz);
  useEffect(() => setLocal(matriz), [matriz]);

  const guardar = useMutation({
    mutationFn: (rol: string) => editarMatriz(rol, local[rol] ?? {}),
    onSuccess: onSaved,
  });

  const toggle = (rol: string, perm: string) =>
    setLocal((m) => ({ ...m, [rol]: { ...m[rol], [perm]: !m[rol]?.[perm] } }));

  return (
    <section className="bg-white border rounded-lg">
      <div className="px-4 py-2 border-b text-sm font-medium text-slate-700">
        Permisos por perfil (configurable)
      </div>
      <div className="p-4 overflow-x-auto">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left font-medium py-2 pr-4">Permiso</th>
              {editables.map((r) => (
                <th key={r.key} className="font-medium px-3 py-2 text-center">{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permisos.map((p) => (
              <tr key={p.key} className="border-t">
                <td className="py-1.5 pr-4 text-slate-700">{p.label}</td>
                {editables.map((r) => (
                  <td key={r.key} className="px-3 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={!!local[r.key]?.[p.key]}
                      onChange={() => toggle(r.key, p.key)}
                      className="w-4 h-4 accent-blue-600"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {editables.map((r) => (
            <button
              key={r.key}
              type="button"
              disabled={guardar.isPending}
              onClick={() => guardar.mutate(r.key)}
              className="bg-slate-800 text-white text-sm rounded px-3 py-1.5 hover:bg-slate-900 disabled:opacity-50"
            >
              Guardar {r.label}
            </button>
          ))}
          {guardar.isError && (
            <span className="text-sm text-red-600">{(guardar.error as Error).message}</span>
          )}
          {guardar.isSuccess && !guardar.isPending && (
            <span className="text-sm text-emerald-600">Guardado.</span>
          )}
        </div>
      </div>
    </section>
  );
}
